class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.listenerController) return;
    this.panel = this.querySelector('.drawer__inner');
    this.items = this.querySelector('cart-drawer-items');
    this.overlay = this.querySelector('#CartDrawer-Overlay');
    this.ready = Boolean(this.panel && this.items && this.overlay && window.routes?.cart_url && window.routes?.cart_change_url);
    if (!this.ready) return;

    this.listenerController = new AbortController();
    const signal = this.listenerController.signal;
    document.addEventListener('theme:cart:refresh', () => {
      this.renderContents().catch(() => {
        this.needsRecovery = true;
        this.showError('We couldn’t refresh your cart. View your full cart to continue.', null, true);
        this.setBusy(false);
      });
    }, { signal });
    this.requestRevision = 0;
    this.activeElement = null;
    this.pendingMutation = null;
    const quickView = document.querySelector('[data-theme-quick-view-shell]');
    quickView?.addEventListener('close', () => {
      // Native close events are queued; a cached dialog may already own a
      // new open cycle when the old close event is delivered.
      if (quickView.open || document.querySelector('dialog[open]') || !this.classList.contains('active')) return;
      const focus = document.activeElement;
      trapFocus(this.panel, focus?.isConnected && this.panel.contains(focus) ? focus : this.querySelector('.drawer__close'));
    }, { signal });
    this.addEventListener('click', (event) => this.onDrawerClick(event), { signal });
    this.addEventListener('change', (event) => this.onQuantityChange(event), { signal });
    this.panel.addEventListener('touchstart', (event) => this.onSwipeStart(event), { passive: true, signal });
    this.panel.addEventListener('touchmove', (event) => this.onSwipeMove(event), { passive: false, signal });
    this.panel.addEventListener('touchend', (event) => this.onSwipeEnd(event), { signal });
    this.panel.addEventListener('touchcancel', () => this.resetSwipe(), { signal });
    this.addEventListener('scroll', (event) => {
      if (event.target === this.items) this.syncScrollCue();
    }, { capture: true, signal });
    this.addEventListener('keydown', (event) => {
      if (this.pendingQuantityFocus && !event.metaKey && !event.ctrlKey && !event.altKey &&
          !['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
        this.pendingQuantityFocus.pointer = false;
      }
      if (event.key === 'Tab' && this.classList.contains('is-loading')) {
        event.preventDefault();
        this.querySelector('.drawer__close').focus();
        return;
      }
      if (event.key === 'Escape' && this.classList.contains('active')) {
        event.preventDefault();
        this.close();
      }
    }, { signal });
    const onViewportResize = () => {
      if (this.classList.contains('is-swipe-dismissing')) {
        this.close();
        return;
      }
      this.resetSwipe();
      if (this.classList.contains('active')) this.syncViewportWidth();
      this.syncScrollCue();
    };
    window.addEventListener('resize', onViewportResize, { signal });
    window.visualViewport?.addEventListener?.('resize', onViewportResize, { signal });
    this.scrollObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => this.syncScrollCue())
      : null;
    this.observeItems();

    document.querySelectorAll('[data-theme-cart-trigger]').forEach((link) => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.setActiveElement(link);
        this.refreshAndOpen().catch(() => {
          window.location.assign(link.href);
        });
      }, { signal });
      link.addEventListener('keydown', (event) => {
        if (event.code !== 'Space') return;
        event.preventDefault();
        this.setActiveElement(link);
        this.refreshAndOpen().catch(() => window.location.assign(link.href));
      }, { signal });
    });
  }

  disconnectedCallback() {
    this.resetSwipe();
    this.listenerController?.abort();
    this.listenerController = null;
    this.scrollObserver?.disconnect();
    this.scrollObserver = null;
    this.close();
    document.dispatchEvent(new CustomEvent('theme:cart:render', { detail: { drawer: this } }));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  syncViewportWidth() {
    const visualViewport = window.visualViewport;
    const visibleRight = visualViewport ? visualViewport.offsetLeft + visualViewport.width : 0;
    this.style.width = `${Math.ceil(Math.max(window.innerWidth, visibleRight))}px`;
  }

  observeItems() {
    this.scrollObserver?.disconnect();
    if (this.items) {
      this.scrollObserver?.observe(this.items);
      const content = this.items.querySelector('.drawer__contents');
      if (content) this.scrollObserver?.observe(content);
    }
    this.syncScrollCue();
  }

  syncScrollCue() {
    if (!this.items) return;
    const shell = this.querySelector('.cart-drawer__items-shell');
    const overflow = Boolean(shell && this.items.scrollHeight > shell.clientHeight);
    const cue = this.querySelector('[data-cart-scroll-cue]');
    if (cue) {
      cue.hidden = !overflow;
      cue.classList.toggle('is-visible', overflow && this.items.scrollTop < 1);
    }
    if (overflow) {
      this.items.setAttribute('tabindex', '0');
      this.items.setAttribute('role', 'region');
      this.items.setAttribute('aria-label', this.items.dataset.cartItemsLabel);
    } else {
      this.items.removeAttribute('tabindex');
      this.items.removeAttribute('role');
      this.items.removeAttribute('aria-label');
    }
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer' }];
  }

  async refreshAndOpen() {
    this.classList.add('is-loading');
    this.querySelector('.cart-drawer__loading').hidden = false;
    this.open();
    await this.prepareContents();
  }

  async prepareContents() {
    if (this.pendingMutation) await this.pendingMutation;
    const revision = ++this.requestRevision;
    const separator = window.routes.cart_url.includes('?') ? '&' : '?';
    const request = (async () => {
      const html = await this.fetchCartSection(separator);
      if (revision === this.requestRevision) this.applySection(html);
    })();
    this.latestRefresh = request;
    try {
      await request;
    } catch (error) {
      if (revision === this.requestRevision) throw error;
    }
    if (revision !== this.requestRevision) {
      if (this.latestRefresh !== request) await this.latestRefresh;
      else if (this.pendingMutation) await this.pendingMutation;
    }
  }

  async fetchCartSection(separator = window.routes.cart_url.includes('?') ? '&' : '?') {
    const response = await fetch(`${window.routes.cart_url}${separator}sections=cart-drawer`, {
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Cart unavailable');
    const sections = await response.json();
    return sections['cart-drawer'];
  }

  async renderContents() {
    if (!this.ready) throw new Error('Cart drawer unavailable');
    await this.prepareContents();
  }

  applySection(html, focusIndex = null, quantityFocus = null) {
    if (typeof html !== 'string') throw new Error('Cart section unavailable');
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
    const sourcePanel = source?.querySelector('.drawer__inner');
    const count = Number(source?.dataset.cartCount);
    if (!sourcePanel || !Number.isSafeInteger(count) || count < 0) throw new Error('Cart section invalid');

    const previousScroll = this.items.scrollTop;
    const previousFocus = document.activeElement;
    window.ThemeCartRender.reconcile(this.panel, sourcePanel);
    this.items = this.querySelector('cart-drawer-items');
    this.items.scrollTop = previousScroll;
    this.observeItems();
    this.dataset.cartCount = String(count);
    document.dispatchEvent(new CustomEvent('theme:cart:render', { detail: { drawer: this } }));
    this.needsRecovery = false;
    this.classList.remove('is-loading');
    this.classList.toggle('is-empty', count === 0);
    document.querySelectorAll('[data-theme-cart-count]').forEach((node) => {
      node.textContent = String(count);
    });

    if (this.classList.contains('active') && !document.querySelector('[data-theme-quick-view-shell][open]')) {
      const quantityInput = focusIndex === null ? null : this.querySelector(`#Drawer-quantity-${focusIndex}`);
      const quantityButton = quantityFocus?.button
        ? quantityInput?.closest('quantity-input')?.querySelector(`button[name="${quantityFocus.button}"]`)
        : null;
      const focusTarget = quantityButton || (focusIndex === null
        ? (previousFocus?.isConnected && this.panel.contains(previousFocus) ? previousFocus : this.querySelector('.drawer__close'))
        : quantityInput || this.querySelector('.drawer__close'));
      trapFocus(this.panel, focusTarget);
      // The replacement arrives after the click task has ended. Retain its
      // pointer origin on the restored button; keyboard input clears it above.
      if (quantityButton && quantityFocus.pointer) {
        quantityButton.setAttribute('data-theme-pointer-focus', '');
      }
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) return;
    this.resetSwipe();
    this.inert = false;
    this.removeAttribute('aria-hidden');
    const canvasWidth = document.body.getBoundingClientRect().width;
    this.bodyOverflowHiddenBeforeOpen = document.body.classList.contains('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    // Measure the space actually released by the lock. A stable root gutter
    // retains it, while unsupported browsers need the padding fallback.
    const scrollbarWidth = Math.max(0, document.body.getBoundingClientRect().width - canvasWidth);
    if (scrollbarWidth > 0) {
      this.bodyPaddingRightBeforeOpen = document.body.style.paddingRight;
      this.bodyPaddingRightPriorityBeforeOpen = document.body.style.getPropertyPriority('padding-right');
      const existingPadding = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.setProperty('padding-right', `${existingPadding + scrollbarWidth}px`, this.bodyPaddingRightPriorityBeforeOpen);
    }
    this.syncViewportWidth();
    this.classList.add('active');
    this.inertBackground();
    this.syncScrollCue();
    if (this.classList.contains('is-loading')) {
      removeTrapFocus();
      this.querySelector('.drawer__close').focus();
    } else {
      trapFocus(this.panel, this.querySelector('.drawer__close'));
    }
  }

  close() {
    if (!this.classList.contains('active')) {
      this.restoreBackground();
      return;
    }
    const swipeCompleted = this.classList.contains('is-swipe-dismissing');
    this.resetSwipe();
    this.classList.toggle('is-swipe-complete', swipeCompleted);
    this.classList.remove('active');
    if (!this.bodyOverflowHiddenBeforeOpen) document.body.classList.remove('overflow-hidden');
    this.bodyOverflowHiddenBeforeOpen = undefined;
    if (this.bodyPaddingRightBeforeOpen !== undefined) {
      document.body.style.setProperty('padding-right', this.bodyPaddingRightBeforeOpen, this.bodyPaddingRightPriorityBeforeOpen);
      this.bodyPaddingRightBeforeOpen = undefined;
      this.bodyPaddingRightPriorityBeforeOpen = undefined;
    }
    this.restoreBackground();
    const focusTarget = this.activeElement?.isConnected ? this.activeElement : null;
    removeTrapFocus();
    if (focusTarget) {
      globalThis.themeFocusModality?.markProgrammaticFocus(focusTarget);
      focusTarget.focus({ preventScroll: true });
    }
    this.activeElement = null;
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
  }

  inertBackground() {
    this.backgroundInert = new Map();
    const inertSibling = (node) => {
      // The native Quick View dialog owns the nested modal's isolation. An
      // explicit inert flag would block it even after showModal().
      if (node.nodeType !== 1 || node === this || node.matches?.('[data-theme-quick-view-shell]') || this.backgroundInert.has(node)) return;
      this.backgroundInert.set(node, node.inert);
      node.inert = true;
    };
    Array.from(document.body.children).forEach(inertSibling);
    if (typeof MutationObserver === 'function') {
      this.backgroundObserver = new MutationObserver((mutations) => {
        if (!this.backgroundInert) return;
        mutations.forEach((mutation) => mutation.addedNodes.forEach(inertSibling));
      });
      this.backgroundObserver.observe(document.body, { childList: true });
    }
  }

  restoreBackground() {
    this.backgroundObserver?.disconnect();
    this.backgroundObserver = null;
    this.backgroundInert?.forEach((wasInert, node) => { node.inert = wasInert; });
    this.backgroundInert = null;
  }

  onSwipeStart(event) {
    if (event.touches.length !== 1) {
      this.resetSwipe();
      return;
    }
    if (!this.classList.contains('active') || this.classList.contains('is-swipe-dismissing') ||
        !window.matchMedia?.('(max-width: 59.99rem)')?.matches ||
        event.target.closest('a, button, input, select, textarea, [role="button"]')) return;
    const touch = event.touches[0];
    this.swipe = { id: touch.identifier, x: touch.clientX, y: touch.clientY,
      lastX: touch.clientX, lastTime: event.timeStamp, velocity: 0, active: false };
  }

  onSwipeMove(event) {
    const swipe = this.swipe;
    if (!swipe) return;
    if (event.touches.length !== 1) {
      this.resetSwipe();
      return;
    }
    const touch = Array.from(event.touches).find((item) => item.identifier === swipe.id);
    if (!touch) return;
    const dx = touch.clientX - swipe.x;
    const dy = touch.clientY - swipe.y;
    if (!swipe.active) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 12) return;
      if (dx <= 0 || Math.abs(dx) < Math.abs(dy) * 1.5) {
        this.swipe = null;
        return;
      }
      swipe.active = true;
      this.classList.add('is-swipe-dragging');
    }
    const elapsed = Math.max(1, event.timeStamp - swipe.lastTime);
    swipe.velocity = Math.max(0, touch.clientX - swipe.lastX) / elapsed;
    swipe.lastX = touch.clientX;
    swipe.lastTime = event.timeStamp;
    this.panel.style.setProperty('--cart-drawer-swipe-x', `${Math.max(0, dx)}px`);
    if (event.cancelable) event.preventDefault();
  }

  onSwipeEnd(event) {
    const swipe = this.swipe;
    if (!swipe || !Array.from(event.changedTouches).some((touch) => touch.identifier === swipe.id)) return;
    this.swipe = null;
    if (!swipe.active) return;
    const distance = Math.max(0, swipe.lastX - swipe.x);
    this.classList.remove('is-swipe-dragging');
    if (distance >= this.panel.getBoundingClientRect().width * 0.3 ||
        (distance >= 48 && swipe.velocity >= 0.5)) {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
        this.close();
      } else {
        this.classList.add('is-swipe-dismissing');
        this.panel.style.setProperty('--cart-drawer-swipe-x', `${this.panel.getBoundingClientRect().width}px`);
        this.swipeCloseTimer = window.setTimeout(() => this.close(), 250);
      }
    } else {
      this.panel.style.removeProperty('--cart-drawer-swipe-x');
    }
  }

  resetSwipe() {
    this.swipe = null;
    if (this.swipeCloseTimer) window.clearTimeout(this.swipeCloseTimer);
    this.swipeCloseTimer = null;
    this.classList.remove('is-swipe-dragging', 'is-swipe-dismissing', 'is-swipe-complete');
    this.panel?.style?.removeProperty('--cart-drawer-swipe-x');
  }

  onDrawerClick(event) {
    if (event.target === this.overlay || event.target.closest('.drawer__close, [data-cart-drawer-close]')) {
      this.close();
      return;
    }
    const remove = event.target.closest('[id^="CartDrawer-Remove-"]');
    if (remove && !this.pendingMutation) {
      event.preventDefault();
      this.changeQuantity(remove.dataset.index, 0);
    }
  }

  onQuantityChange(event) {
    const input = event.target.closest('#CartDrawer .quantity__input');
    if (!input || this.pendingMutation) return;
    const quantity = Number(input.value);
    if (!Number.isSafeInteger(quantity) || quantity < 0) {
      this.showError(window.cartStrings?.error || 'Unable to update cart.', input.dataset.index);
      return;
    }
    const button = event.detail?.quantityButton;
    const quantityFocus = ['plus', 'minus'].includes(button)
      ? { button, pointer: event.detail.pointer === true }
      : null;
    this.changeQuantity(input.dataset.index, quantity, quantityFocus);
  }

  changeQuantity(index, quantity, quantityFocus = null) {
    if (this.pendingMutation) return;
    const line = Number(index);
    if (!Number.isSafeInteger(line) || line < 1) return;
    const row = this.querySelector(`#CartDrawer-Item-${line}`);
    const item = { key: row?.dataset.themeItemKey, title: row?.querySelector('.cart-item__name')?.textContent.trim() };
    const status = this.querySelector('[data-theme-cart-status]');
    if (status) status.textContent = '';
    const revision = ++this.requestRevision;
    this.pendingQuantityFocus = quantityFocus;
    this.setBusy(true);
    this.showError('');
    let mutationAccepted = false;
    let failureMessage = window.cartStrings?.error || 'Unable to update cart.';
    const update = async () => {
      const response = await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          line,
          quantity,
          sections: ['cart-drawer'],
          sections_url: window.routes.cart_url
        })
      });
      const state = await response.json();
      if (!response.ok || state.status || state.errors) {
        failureMessage = state.description || (typeof state.errors === 'string' && state.errors) ||
          state.message || failureMessage;
        throw new Error(failureMessage);
      }
      mutationAccepted = true;
      if (revision !== this.requestRevision) return;
      this.applySection(state.sections?.['cart-drawer'], line, quantityFocus);
      this.reportQuantityChange(line, quantity, item);
    };
    this.pendingMutation = update().catch(async () => {
      if (revision !== this.requestRevision) return;
      // An API rejection can leave the input optimistic, and a lost response can
      // leave the mutation's outcome unknown. Confirm Shopify's cart before retry.
      try {
        const html = await this.fetchCartSection();
        if (revision !== this.requestRevision) return;
        this.applySection(html, line, quantityFocus);
        if (mutationAccepted) {
          this.reportQuantityChange(line, quantity, item);
        } else {
          this.showError(failureMessage, line);
        }
      } catch {
        this.needsRecovery = true;
        this.showError(mutationAccepted
          ? 'Cart updated, but could not refresh. View your full cart to continue.'
          : failureMessage, null, true);
      }
    }).finally(() => {
      this.pendingMutation = null;
      this.pendingQuantityFocus = null;
      this.setBusy(false);
    });
  }

  reportQuantityChange(line, quantity, item) {
    const row = [...this.querySelectorAll('.cart-item[data-theme-item-key]')]
      .find((row) => row.dataset.themeItemKey === item.key);
    const actual = row?.querySelector('.quantity__input');
    const confirmedQuantity = actual ? Number(actual.value) : 0;
    if (confirmedQuantity !== quantity) {
      this.showError(window.cartStrings?.quantityError?.replace('[quantity]', confirmedQuantity) || 'Quantity unavailable.', line);
    } else {
      window.ThemeCartRender.announce(this.querySelector('[data-theme-cart-status]'), { title: item.title, quantity: confirmedQuantity });
    }
  }

  setBusy(busy) {
    this.querySelectorAll('cart-drawer-items button, cart-drawer-items input').forEach((control) => {
      control.disabled = busy || this.needsRecovery;
    });
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = busy || this.needsRecovery || this.dataset.cartCount === '0';
    this.items?.setAttribute('aria-busy', String(busy));
  }

  showError(message, line = null, recovery = false) {
    if (message) {
      const status = this.querySelector('[data-theme-cart-status]');
      if (status) status.textContent = '';
    }
    const lineError = message && line !== null && !recovery
      ? this.querySelector(`#CartDrawer-LineItemError-${line} .cart-item__error-text`)
      : null;
    const region = this.querySelector('#CartDrawer-CartErrors');
    if (region) {
      region.hidden = !message || Boolean(lineError);
      region.querySelector('[data-cart-error-message]').textContent = message;
      region.querySelector('[data-cart-recovery]').hidden = !recovery;
    }
    this.querySelectorAll('.cart-item__error-text').forEach((node) => { node.textContent = ''; });
    if (lineError) lineError.textContent = message;
  }
}

customElements.define('cart-drawer', CartDrawer);
